<aside
  class="flex flex-col bg-navy-900 transition-all duration-200 shrink-0 fixed inset-y-0 left-0 z-50 h-full w-54 sm:relative sm:translate-x-0 sm:w-16 sm:z-30"
  [ngClass]="{
    'translate-x-0': layout.mobileOpen(),
    '-translate-x-full': !layout.mobileOpen(),
    'lg:w-54': !layout.collapsed(),
    'lg:w-16': layout.collapsed(),
  }"
>
  <!-- Logo -->
  <div class="py-8 flex items-center justify-center overflow-hidden">
    <img
      src="/icons/logo.svg"
      alt="JCCA"
      [class.mx-auto]="layout.collapsed()"
      class="h-6 shrink-0 sm:mx-auto"
    />
  </div>

  <!-- Nav -->
  <nav class="flex-1 px-3 py-4 space-y-0.5 overflow-hidden">
    @if (!layout.collapsed()) {
      <p
        class="text-slate-400 text-2xs font-semibold uppercase tracking-widest px-3 mb-6 sm:hidden lg:block"
      >
        Menu
      </p>
    }

    @for (item of navItems(); track item.route) {
      <a
        [routerLink]="item.route"
        routerLinkActive="bg-navy-800 !text-accent-500 font-bold"
        [routerLinkActiveOptions]="{ exact: item.route === '/adasas' }"
        class="flex items-center gap-3 py-3 rounded-2xl text-slate-400 hover:bg-navy-700 transition-colors text-sm leading-4 tracking-normal"
        [class]="
          !layout.collapsed()
            ? 'px-5 sm:px-2 sm:flex sm:justify-center lg:px-5 lg:justify-start'
            : 'px-2 flex justify-center'
        "
        (click)="layout.mobileOpen() && layout.mobileOpen.set(false)"
      >
        <span
          class="w-4 h-4 shrink-0 block bg-current icon-mask"
          [style.maskImage]="'url(' + item.iconUrl + ')'"
          [style.webkitMaskImage]="'url(' + item.iconUrl + ')'"
        ></span>
        @if (!layout.collapsed()) {
          <span class="sm:hidden lg:block">{{ item.label }}</span>
        }
      </a>
    }
  </nav>

  <!-- Collapse toggle -->
  <div
    class="absolute bottom-28 -right-6 z-50 rounded-full w-11 h-11 bg-white hidden lg:flex justify-center items-center"
  >
    <button
      (click)="layout.collapsed.set(!layout.collapsed())"
      class="w-8 h-8 rounded-full bg-navy-900 border border-white/10 hover:bg-navy-700 flex items-center justify-center text-white/60 hover:text-white transition-colors cursor-pointer shadow-md"
    >
      <svg
        class="w-4 h-4 transition-transform duration-200"
        [class.rotate-180]="layout.collapsed()"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
      </svg>
    </button>
  </div>

  <!-- User -->
  <div class="px-3 pb-4 pt-2 overflow-visible">
    @if (layout.collapsed()) {
      <!-- Collapsed: avatar button + floating popup -->
      <div class="relative flex justify-center">
        <button
          (click)="userMenuOpen.set(!userMenuOpen())"
          class="w-9 h-9 rounded-full bg-accent-500 flex items-center justify-center text-xs font-bold text-white cursor-pointer hover:opacity-90 transition-opacity"
        >
          {{ userInitials || '?' }}
        </button>

        @if (userMenuOpen()) {
          <!-- Backdrop -->
          <div class="fixed inset-0 z-20" (click)="userMenuOpen.set(false)"></div>

          <!-- Popup -->
          <div
            class="absolute bottom-full left-full mb-2 ml-2 z-30 w-52 bg-navy-800 rounded-2xl shadow-xl overflow-hidden border border-white/10"
          >
            <!-- User info -->
            <div class="flex items-center gap-3 px-4 py-3 border-b border-white/10">
              <div
                class="w-9 h-9 rounded-full bg-accent-500 flex items-center justify-center text-xs font-bold text-white shrink-0"
              >
                {{ userInitials || '?' }}
              </div>
              <div class="min-w-0">
                <p class="text-sm font-semibold text-white truncate leading-4">
                  {{ authService.currentUser()?.firstName }}
                </p>
                <p class="text-xs text-white/50 truncate mt-0.5">
                  {{ authService.getUserRole() | titlecase }}
                </p>
              </div>
            </div>
            <!-- Actions -->
            <button
              (click)="logout(); userMenuOpen.set(false)"
              class="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/70 hover:bg-white/10 hover:text-white transition-colors cursor-pointer"
            >
              <svg
                class="w-4 h-4 shrink-0"
                fill="none"
                stroke="currentColor"
                stroke-width="1.75"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Sign out
            </button>
          </div>
        }
      </div>
    } @else {
      <!-- Avatar only on tablet (sidebar is narrow but collapsed() is false) -->
      <div class="flex lg:hidden justify-center py-1">
        <div
          class="w-9 h-9 rounded-full bg-accent-500 flex items-center justify-center text-xs font-bold text-white shrink-0"
        >
          {{ userInitials || '?' }}
        </div>
      </div>
      <!-- Full card on desktop -->
      <div class="hidden lg:flex items-center gap-3 bg-navy-800 rounded-2xl px-3 py-3">
        <div
          class="w-9 h-9 rounded-full bg-accent-500 flex items-center justify-center text-xs font-bold text-white shrink-0"
        >
          {{ userInitials || '?' }}
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-white truncate leading-4">
            {{ authService.currentUser()?.firstName }}
          </p>
          <p class="text-xs text-white/50 truncate mt-0.5">
            {{ authService.getUserRole() | titlecase }}
          </p>
        </div>
        <button
          (click)="logout()"
          title="Sign out"
          class="text-white/30 hover:text-white transition-colors cursor-pointer shrink-0 ml-1"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
            />
          </svg>
        </button>
      </div>
    }
  </div>
</aside>
